import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '@/api/http';
import { AdminPageHeader } from '@/components/admin/admin-shell';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/admin/cancelamentos')({ component: AdminCancelamentos });

type TabKey = 'pending' | 'history';

type RefundItem = {
  id: string;                    // Payment.id
  amount: number;
  method: string | null;         // CARD | PIX
  refundStatus: 'REQUESTED' | 'PROCESSING' | 'DONE' | 'FAILED';
  refundAmount: number | null;
  refundPixKey: string | null;
  refundPixKeyType: string | null;
  refundRequestedAt: string | null;
  refundedAt: string | null;
  refundMethod: string | null;   // INTER_AUTO | MANUAL_OUT
  refundInterEndToEndId: string | null;
  refundInterCorrelationId: string | null;
  refundNote: string | null;
  refundedByAdminId: string | null;
  session: {
    id: string;
    scheduledAt: string;
    durationMin: number;
    modality: string;
    cancellationReason: string | null;
    cancelledBy: 'CLIENT' | 'THERAPIST' | null;
    therapist: { user: { name: string | null; image: string | null } };
  };
  client: {
    id: string;
    user: { name: string | null; email: string; image: string | null };
  };
};

const money = (v: any) => Number(v ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const STATUS_META: Record<RefundItem['refundStatus'], { label: string; cls: string }> = {
  REQUESTED:  { label: 'Solicitado',   cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  PROCESSING: { label: 'Processando',  cls: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
  DONE:       { label: 'Concluído',    cls: 'bg-teal-50 text-teal-700 border-teal-200' },
  FAILED:     { label: 'Falhou',       cls: 'bg-red-50 text-red-700 border-red-200' },
};

function AdminCancelamentos() {
  const qc = useQueryClient();
  const [tab, setTab] = useState<TabKey>('pending');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [errorMsgById, setErrorMsgById] = useState<Record<string, string>>({});
  const [manualNoteById, setManualNoteById] = useState<Record<string, string>>({});

  const { data, isLoading } = useQuery({
    queryKey: ['admin-refunds', tab],
    queryFn: () =>
      api.get(tab === 'pending' ? 'admin/refunds' : 'admin/refunds/history').json<RefundItem[]>(),
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['admin-refunds'] });
    qc.invalidateQueries({ queryKey: ['admin-stats'] });
  };

  const setErr = (id: string, msg: string | null) =>
    setErrorMsgById((p) => ({ ...p, [id]: msg ?? '' }));

  const executeInter = useMutation({
    mutationFn: (paymentId: string) =>
      api.post(`admin/refunds/${paymentId}/execute-inter`).json<{ ok: true }>(),
    onSuccess: () => {
      invalidate();
      alert('PIX enviado via Inter. Reembolso movido para o Histórico.');
    },
    onError: async (err: any, paymentId) => {
      let msg = 'Falha ao enviar PIX via Inter.';
      try { const body = await err?.response?.json?.(); if (body?.message) msg = body.message; } catch {}
      setErr(paymentId, msg);
      alert(`Falha ao enviar PIX: ${msg}`);
    },
  });

  const [debugData, setDebugData] = useState<any[] | null>(null);
  const debug = useMutation({
    mutationFn: () => api.get('admin/refunds/debug').json<any[]>(),
    onSuccess: (rows) => setDebugData(rows),
    onError: async (err: any) => {
      let msg = 'Falha no diagnóstico.';
      try { const body = await err?.response?.json?.(); if (body?.message) msg = body.message; } catch {}
      alert(msg);
    },
  });

  const [pixDraftById, setPixDraftById] = useState<Record<string, { key: string; type: string }>>({});
  const updatePixKey = useMutation({
    mutationFn: (vars: { paymentId: string; pixKey: string; pixKeyType: string }) =>
      api.post(`admin/refunds/${vars.paymentId}/pix-key`, { json: { pixKey: vars.pixKey, pixKeyType: vars.pixKeyType } }).json<{ ok: true }>(),
    onSuccess: (_r, vars) => {
      invalidate();
      setPixDraftById((p) => { const n = { ...p }; delete n[vars.paymentId]; return n; });
      alert('Chave PIX salva. Agora o botão "💸 PIX via Inter" pode ser usado.');
    },
    onError: async (err: any, vars) => {
      let msg = 'Falha ao salvar chave PIX.';
      try { const body = await err?.response?.json?.(); if (body?.message) msg = body.message; } catch {}
      setErr(vars.paymentId, msg);
      alert(`Falha ao salvar: ${msg}`);
    },
  });

  const backfill = useMutation({
    mutationFn: () => api.post('admin/refunds/backfill').json<{ migrated: number; candidates: number }>(),
    onSuccess: (r) => {
      invalidate();
      alert(
        r.migrated > 0
          ? `${r.migrated} reembolso(s) órfão(s) recuperado(s) e agora aparecem como Solicitado.`
          : 'Nenhum reembolso órfão encontrado.',
      );
    },
    onError: async (err: any) => {
      let msg = 'Falha no backfill.';
      try { const body = await err?.response?.json?.(); if (body?.message) msg = body.message; } catch {}
      alert(msg);
    },
  });

  const markManual = useMutation({
    mutationFn: (vars: { paymentId: string; note: string }) =>
      api.post(`admin/refunds/${vars.paymentId}/mark-manual`, { json: { note: vars.note } }).json<{ ok: true }>(),
    onSuccess: (_r, vars) => {
      invalidate();
      setManualNoteById((p) => ({ ...p, [vars.paymentId]: '' }));
      alert('Reembolso marcado como pago manualmente.');
    },
    onError: async (err: any, vars) => {
      let msg = 'Falha ao marcar reembolso manual.';
      try { const body = await err?.response?.json?.(); if (body?.message) msg = body.message; } catch {}
      setErr(vars.paymentId, msg);
      alert(`Falha: ${msg}`);
    },
  });

  const items = data ?? [];

  return (
    <>
      <AdminPageHeader
        title="Cancelamentos & Reembolsos"
        subtitle={
          tab === 'pending'
            ? `${items.length} aguardando reembolso · PIX via Banco Inter ou manual`
            : `${items.length} reembolsos concluídos`
        }
      />

      <div className="flex gap-2 mb-4 flex-wrap items-center justify-between">
        <div className="flex gap-2 flex-wrap">
          {(['pending', 'history'] as TabKey[]).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={cn(
                'px-4 py-1.5 rounded-full text-sm font-medium transition border',
                tab === t
                  ? 'bg-slate-900 text-white border-slate-900'
                  : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300',
              )}
            >
              {t === 'pending' ? 'Pendentes' : 'Histórico'}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => debug.mutate()}
            disabled={debug.isPending}
            className="text-[11px] text-slate-500 hover:text-slate-700 underline"
            title="Últimas 30 sessões CANCELLED e estado bruto do Payment"
          >
            {debug.isPending ? 'Carregando…' : '🔎 Diagnóstico'}
          </button>
          <button
            onClick={() => {
              if (confirm('Buscar reembolsos "órfãos" (cancelados antes do deploy) e migrar pra Solicitado?\n\nIdempotente — só afeta pagamentos com refundStatus=NONE.')) {
                backfill.mutate();
              }
            }}
            disabled={backfill.isPending}
            className="text-[11px] text-slate-500 hover:text-slate-700 underline"
            title="Migra Payments PAID com refundPixKey preenchida mas sem refundStatus"
          >
            {backfill.isPending ? 'Buscando…' : '↻ Recuperar reembolsos órfãos'}
          </button>
        </div>
      </div>

      {debugData && (
        <div className="mb-4 bg-slate-900 text-slate-100 rounded-2xl p-4 text-[11px] font-mono overflow-x-auto max-h-[400px]">
          <div className="flex items-center justify-between mb-2 sticky top-0 bg-slate-900 pb-1">
            <span className="text-slate-400">{debugData.length} sessões canceladas (mais recentes primeiro)</span>
            <button onClick={() => setDebugData(null)} className="text-slate-400 hover:text-white">✕ fechar</button>
          </div>
          <pre className="whitespace-pre-wrap break-words">{JSON.stringify(debugData, null, 2)}</pre>
        </div>
      )}

      <div className="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="p-12 text-center text-sm text-slate-400">Carregando...</div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center text-sm text-slate-400">
            {tab === 'pending'
              ? 'Nenhum reembolso pendente. Quando um cliente cancelar uma sessão paga, aparece aqui.'
              : 'Nenhum reembolso concluído ainda.'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[960px]">
              <thead className="bg-slate-50 text-slate-500 uppercase tracking-wide text-[10px]">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold">Cliente</th>
                  <th className="text-left px-4 py-3 font-semibold">Sessão</th>
                  <th className="text-right px-4 py-3 font-semibold">Valor pago</th>
                  <th className="text-right px-4 py-3 font-semibold">Reembolso</th>
                  <th className="text-left px-4 py-3 font-semibold">Chave PIX</th>
                  <th className="text-left px-4 py-3 font-semibold">Status</th>
                  <th className="text-right px-4 py-3 font-semibold">Ação</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((r) => {
                  const isExpanded = expandedId === r.id;
                  const meta = STATUS_META[r.refundStatus];
                  const err = errorMsgById[r.id];
                  const note = manualNoteById[r.id] ?? '';
                  const canAct = r.refundStatus === 'REQUESTED' || r.refundStatus === 'FAILED';
                  return (
                    <>
                      <tr key={r.id} className="hover:bg-slate-50/60">
                        <td className="px-4 py-3 min-w-0">
                          <div className="font-semibold text-slate-800 text-xs leading-tight truncate">
                            {r.client.user.name ?? '—'}
                          </div>
                          <div className="text-[11px] text-slate-400 truncate">{r.client.user.email}</div>
                        </td>
                        <td className="px-4 py-3 text-xs text-slate-700">
                          <div>{new Date(r.session.scheduledAt).toLocaleString('pt-BR')}</div>
                          <div className="text-[11px] text-slate-400">
                            com {r.session.therapist.user.name ?? '—'} · {r.session.modality}
                          </div>
                          <button
                            type="button"
                            onClick={() => setExpandedId(isExpanded ? null : r.id)}
                            className="text-[11px] text-teal-600 hover:underline mt-0.5"
                          >
                            {isExpanded ? '▾ Ocultar motivo' : '▸ Ver motivo'}
                          </button>
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums text-slate-600 text-xs">
                          {money(r.amount)}
                          <div className="text-[10px] text-slate-400 mt-0.5">{r.method ?? '—'}</div>
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums font-bold text-teal-700">
                          {money(r.refundAmount)}
                          {r.method === 'CARD' && (
                            <div className="text-[10px] font-normal text-slate-400 mt-0.5">
                              taxa Stripe absorvida
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {r.refundPixKey ? (
                            <div className="text-xs">
                              <div className="text-slate-400 text-[10px] uppercase">{r.refundPixKeyType}</div>
                              <div className="font-mono text-slate-700 truncate max-w-[180px]" title={r.refundPixKey}>
                                {r.refundPixKey}
                              </div>
                            </div>
                          ) : (
                            <button
                              type="button"
                              onClick={() => setExpandedId(isExpanded ? null : r.id)}
                              className="text-[11px] text-red-600 hover:underline"
                              title="Cliente cancelou sem informar chave (bug antigo). Colete manualmente e adicione."
                            >
                              ⚠ Sem chave — coletar
                            </button>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span className={cn('text-[10px] px-2 py-0.5 rounded border whitespace-nowrap', meta.cls)}>
                            {meta.label}
                          </span>
                          {r.refundedAt && (
                            <div className="text-[10px] text-slate-400 mt-0.5">
                              {new Date(r.refundedAt).toLocaleDateString('pt-BR')} ·{' '}
                              {r.refundMethod === 'INTER_AUTO' ? 'Inter' : 'Manual'}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          {canAct ? (
                            <div className="flex flex-col items-end gap-1.5">
                              <Button
                                size="sm"
                                onClick={() => {
                                  setErr(r.id, null);
                                  executeInter.mutate(r.id);
                                }}
                                disabled={(executeInter.isPending && executeInter.variables === r.id) || !r.refundPixKey}
                                className="h-7 text-xs px-2.5 bg-teal-500 hover:bg-teal-600 text-white"
                              >
                                {executeInter.isPending && executeInter.variables === r.id ? 'Enviando…' : '💸 PIX via Inter'}
                              </Button>
                              <button
                                type="button"
                                onClick={() => setExpandedId(isExpanded ? null : r.id)}
                                className="text-[10px] text-slate-500 hover:text-slate-700 underline"
                              >
                                ou marcar manual…
                              </button>
                            </div>
                          ) : r.refundInterEndToEndId ? (
                            <code
                              className="text-[10px] text-slate-500 font-mono"
                              title={r.refundInterEndToEndId}
                            >
                              {r.refundInterEndToEndId.slice(0, 14)}…
                            </code>
                          ) : null}
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr key={`${r.id}-detail`}>
                          <td colSpan={7} className="bg-slate-50/50 px-4 py-4">
                            <div className="grid gap-4 md:grid-cols-2 max-w-4xl">
                              <div>
                                <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                                  Motivo do cancelamento
                                </p>
                                <p className="mt-1 text-sm text-slate-700 whitespace-pre-wrap">
                                  {r.session.cancellationReason ?? '—'}
                                </p>
                                <p className="mt-2 text-[11px] text-slate-500">
                                  Cancelado por:{' '}
                                  <strong>
                                    {r.session.cancelledBy === 'CLIENT'
                                      ? 'Cliente'
                                      : r.session.cancelledBy === 'THERAPIST'
                                        ? 'Profissional'
                                        : '—'}
                                  </strong>
                                  {r.refundRequestedAt && (
                                    <>
                                      {' · '}Solicitado em{' '}
                                      {new Date(r.refundRequestedAt).toLocaleString('pt-BR')}
                                    </>
                                  )}
                                </p>
                              </div>

                              {canAct && !r.refundPixKey && (
                                <div className="md:col-span-2 bg-amber-50 border border-amber-200 rounded-md p-3">
                                  <p className="text-[10px] uppercase tracking-wider text-amber-800 font-semibold">
                                    Chave PIX ausente — coletar com o cliente
                                  </p>
                                  <p className="text-[11px] text-amber-700 mt-1">
                                    Este cancel foi registrado antes do fluxo novo persistir a chave. Contate o cliente
                                    ({r.client.user.email}), colete a chave PIX e cadastre abaixo para depois enviar via Inter.
                                  </p>
                                  <div className="mt-2 flex items-center gap-2 flex-wrap">
                                    <select
                                      value={pixDraftById[r.id]?.type ?? ''}
                                      onChange={(e) => setPixDraftById((p) => ({ ...p, [r.id]: { key: p[r.id]?.key ?? '', type: e.target.value } }))}
                                      className="bg-white border border-slate-200 rounded-md h-9 px-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-300"
                                    >
                                      <option value="">Tipo…</option>
                                      <option value="CPF">CPF</option>
                                      <option value="CNPJ">CNPJ</option>
                                      <option value="EMAIL">E-mail</option>
                                      <option value="PHONE">Telefone</option>
                                      <option value="EVP">Aleatória (EVP)</option>
                                    </select>
                                    <input
                                      value={pixDraftById[r.id]?.key ?? ''}
                                      onChange={(e) => setPixDraftById((p) => ({ ...p, [r.id]: { key: e.target.value, type: p[r.id]?.type ?? '' } }))}
                                      placeholder="Chave PIX"
                                      className="flex-1 min-w-[220px] bg-white border border-slate-200 rounded-md h-9 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-teal-300"
                                    />
                                    <Button
                                      size="sm"
                                      onClick={() => {
                                        setErr(r.id, null);
                                        const d = pixDraftById[r.id];
                                        if (!d?.key?.trim() || !d?.type) return;
                                        updatePixKey.mutate({ paymentId: r.id, pixKey: d.key, pixKeyType: d.type });
                                      }}
                                      disabled={(updatePixKey.isPending && updatePixKey.variables?.paymentId === r.id) || !pixDraftById[r.id]?.key?.trim() || !pixDraftById[r.id]?.type}
                                      className="h-9 text-xs bg-amber-600 hover:bg-amber-700 text-white shrink-0"
                                    >
                                      Salvar chave
                                    </Button>
                                  </div>
                                </div>
                              )}

                              {canAct && (
                                <div>
                                  <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                                    Registrar reembolso manual
                                  </p>
                                  <p className="text-[11px] text-slate-500 mt-1">
                                    Use quando fizer o PIX fora da plataforma. Descreva TXID ou nº do comprovante.
                                  </p>
                                  <div className="mt-2 flex items-start gap-2">
                                    <input
                                      value={note}
                                      onChange={(e) =>
                                        setManualNoteById((p) => ({ ...p, [r.id]: e.target.value }))
                                      }
                                      placeholder="Ex: TXID E92... / Comprovante nº 12345"
                                      className="flex-1 bg-white border border-slate-200 rounded-md h-9 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-teal-300"
                                    />
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => {
                                        setErr(r.id, null);
                                        markManual.mutate({ paymentId: r.id, note });
                                      }}
                                      disabled={(markManual.isPending && markManual.variables?.paymentId === r.id) || note.trim().length < 3}
                                      className="h-9 text-xs shrink-0"
                                    >
                                      Confirmar manual
                                    </Button>
                                  </div>
                                </div>
                              )}

                              {r.refundNote && (
                                <div className="md:col-span-2">
                                  <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                                    Nota do reembolso
                                  </p>
                                  <p className="mt-1 text-sm text-slate-700 whitespace-pre-wrap">{r.refundNote}</p>
                                </div>
                              )}

                              {err && (
                                <div className="md:col-span-2 rounded-md bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700">
                                  {err}
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
